/**
 * add_price_controller.js — quote "Add Price" form submit (Stimulus).
 *
 * Ports the turbo:load-bound #addPriceForm submit handler from quote_detail.js to
 * an element-scoped controller so it binds whenever the form enters the DOM,
 * including via an in-place tab-pane frame swap (where turbo:load never fires).
 *
 * this.element is the #addPriceForm <form>. On submit it POSTs to
 * /quote/add_price/<id> (with mark_quoted=1 when "Save & Mark as Quoted" was
 * the button pressed), closes the Bootstrap modal, and applies the returned
 * Turbo Stream so the page totals/line items update in place.
 *
 * One live editor at a time: opening the modal runs requestOpen() from the
 * shared coordination protocol (inline_editor_coordination.js) via Bootstrap's
 * cancelable show.bs.modal, so an open inline editor commits its draft first;
 * a peer veto cancels the show.
 *
 * Wire with data-controller="add-price" on the form.
 */
import { Controller } from "@hotwired/stimulus";
import { requestOpen } from "./inline_editor_coordination.js";

export default class extends Controller {
    connect() {
        this._onSubmit = (e) => this._submit(e);
        this.element.addEventListener('submit', this._onSubmit);

        // show.bs.modal is cancelable: a coordination veto cancels the show
        // and leaves the user in the editor that refused.
        this._modal = this.element.closest('.modal');
        if (this._modal) {
            this._onModalShow = (e) => {
                if (!requestOpen(this.element)) e.preventDefault();
            };
            this._modal.addEventListener('show.bs.modal', this._onModalShow);
        }
    }

    disconnect() {
        if (this._onSubmit) {
            this.element.removeEventListener('submit', this._onSubmit);
            this._onSubmit = null;
        }
        if (this._modal && this._onModalShow) {
            this._modal.removeEventListener('show.bs.modal', this._onModalShow);
            this._onModalShow = null;
            this._modal = null;
        }
    }

    async _submit(e) {
        e.preventDefault();

        const form = this.element;
        const formData = new FormData(form);
        // FormData omits the clicked button; "Save & Mark as Quoted" carries
        // mark_quoted=1 on its name/value, so add the submitter by hand.
        if (e.submitter && e.submitter.name) {
            formData.append(e.submitter.name, e.submitter.value);
        }
        const quoteId = formData.get('quote_id');

        try {
            const response = await fetch(`/quote/add_price/${quoteId}`, {
                method: 'POST',
                body: formData,
                credentials: 'same-origin'
            });

            if (!response.ok) {
                window.showErrorMessage('Error adding price. Please try again.');
                return;
            }

            // Close modal
            const modalEl = document.getElementById('addPriceModal');
            const modal = modalEl && bootstrap.Modal.getInstance(modalEl);
            if (modal) modal.hide();

            // Apply Turbo Stream response to update page components
            const contentType = response.headers.get('content-type') || '';
            if (contentType.includes('turbo-stream')) {
                const html = await response.text();
                Turbo.renderStreamMessage(html);
            } else {
                location.reload();
            }
        } catch (error) {
            console.error('Error adding price:', error);
            window.showErrorMessage('Error adding price. Please try again.');
        }
    }
}
